/**
 * Lender logo resolution.
 *
 * We don't bundle logo files. Instead we resolve each lender to a domain and
 * pull its logo from a logo CDN (Clearbit-style). The <LenderLogo> component
 * falls back to a text chip via onError, so a missing/blocked logo never looks
 * broken — and if the CDN is unavailable, we simply show chips everywhere.
 *
 * To switch providers (e.g. to Brandfetch with a client id), change LOGO_CDN
 * only — nothing else needs to move.
 */

// Logo source:
// - If VITE_BRANDFETCH_CLIENT_ID is set → Brandfetch Logo API (real wordmark
//   logos, always up to date). The client id is public (it goes in the URL).
// - Otherwise → unavatar.io (free, no key; aggregates a brand logo by domain).
// Either way, <LenderLogo>'s onError falls back to a text chip, so a missing
// logo never looks broken. (Clearbit's free logo API was deprecated in 2024.)
const BRANDFETCH_ID = import.meta.env.VITE_BRANDFETCH_CLIENT_ID || ''

const LOGO_CDN = (domain: string) =>
  BRANDFETCH_ID
    ? `https://cdn.brandfetch.io/${domain}/w/400/h/120?c=${BRANDFETCH_ID}`
    : `https://unavatar.io/${domain}?fallback=false`

// Known Australian lender domains (best-effort; unmapped names fall back to a
// slug guess, which just shows a chip if wrong).
const DOMAINS: Record<string, string> = {
  'commonwealth bank': 'commbank.com.au',
  westpac: 'westpac.com.au',
  anz: 'anz.com.au',
  nab: 'nab.com.au',
  'macquarie bank': 'macquarie.com.au',
  'bank australia': 'bankaustralia.com.au',
  'bank of china': 'bankofchina.com',
  ownhome: 'ownhome.com',
  'newcastle permanent': 'newcastlepermanent.com.au',
  'me bank': 'mebank.com.au',
  ing: 'ing.com.au',
  'bank of queensland': 'boq.com.au',
  'mystate bank': 'mystate.com.au',
  'auswide bank': 'auswidebank.com.au',
  'people first bank': 'peoplefirstbank.com.au',
  'better choice': 'betterchoice.com.au',
  suncorp: 'suncorp.com.au',
  'bendigo bank': 'bendigobank.com.au',
  ubank: 'ubank.com.au',
  'bank of us': 'bankofus.com.au',
  bankfirst: 'bankfirst.com.au',
  'bank of sydney': 'banksyd.com.au',
  'p&n bank': 'pnbank.com.au',
  'great southern bank': 'greatsouthernbank.com.au',
  'beyond bank': 'beyondbank.com.au',
  bankwest: 'bankwest.com.au',
  'australian military bank': 'australianmilitarybank.com.au',
  'st.george': 'stgeorge.com.au',
  'bank of melbourne': 'bankofmelbourne.com.au',
  banksa: 'banksa.com.au',
  'teachers mutual bank': 'tmbank.com.au',
  unibank: 'unibank.com.au',
  'health professionals bank': 'hpbank.com.au',
  'firefighters mutual bank': 'fmbank.com.au',
  'granite home loans': 'granitehomeloans.com.au',
  firstmac: 'firstmac.com.au',
  'mortgage mart': 'mortgagemart.com.au',
  'apollo by lmg': 'lmg.broker',
  amp: 'amp.com.au',
  resimac: 'resimac.com.au',
  'australian financial': 'austfinancial.com.au',
  'e-zy mortgage': 'ezymortgage.com.au',
  'la trobe financial': 'latrobefinancial.com.au',
  hejaz: 'hejazfs.com.au',
  'zeus by lmg': 'lmg.broker',
  'credit union sa': 'creditunionsa.com.au',
}

// Domains that Brandfetch has NO logo for — it serves a 200 response with a
// ~594-byte transparent placeholder (not a 404), so <LenderLogo>'s onError can't
// catch it and we'd render a blank box. We route these straight to the text chip
// instead. Found empirically by byte-size sweep of the map; if a newly-added
// lender shows a blank logo, add its domain here.
const NO_LOGO = new Set<string>([
  'bankaustralia.com.au',
  'mortgagemart.com.au',
  'austfinancial.com.au',
  'ezymortgage.com.au',
])

function slugDomainGuess(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '')
  return slug ? `${slug}.com.au` : ''
}

/** Domain for a lender name (mapped, else a best-effort slug guess). */
export function lenderDomain(name: string): string {
  const key = (name || '').trim().toLowerCase()
  if (!key) return ''
  return DOMAINS[key] || slugDomainGuess(name)
}

/** Logo CDN URL for a lender name, or '' if no logo is available (→ text chip). */
export function lenderLogoUrl(name: string): string {
  const domain = lenderDomain(name)
  if (!domain || NO_LOGO.has(domain)) return ''
  return LOGO_CDN(domain)
}
